import { useLayoutContext } from 'contexts/LayoutContext';
import { CalendarDay } from 'further-ui/utils';
import { usePagination } from 'further-ui/hooks';
import { SortOrder } from 'further-types/utils';
import api from 'lib/trpcClient';
import { useLocation, useParams } from 'react-router-dom';
import { usePostUnrealisedFxGainsContext } from './PostUnrealisedFxGainsContext';

export default function usePostUnrealisedFxGainsData() {
  const { selectedVehicleId } = useLayoutContext();
  const { search } = useLocation();
  const { periodId } = useParams<{ periodId?: string }>();

  const searchParams = new URLSearchParams(search);
  const periodStartSearchParam = searchParams.get('periodStart');
  const periodEndSearchParam = searchParams.get('periodEnd');
  const periodStart = periodStartSearchParam
    ? new CalendarDay(periodStartSearchParam)
    : undefined;
  const periodEnd = periodEndSearchParam
    ? new CalendarDay(periodEndSearchParam)
    : undefined;

  const { currencyRateOverrides } = usePostUnrealisedFxGainsContext();

  const payablesPagination = usePagination({
    id: 'post-unrealised-fx-gains',
    order: SortOrder.Desc,
    orderBy: 'transactionDate',
  });

  const confirmedPayablesPagination = usePagination<
    'createdAt' | 'updatedAt' | 'ledgerDate'
  >({
    id: 'post-unrealised-fx-gains-confirmed',
    order: SortOrder.Desc,
    orderBy: 'ledgerDate',
  });

  const confirmedCashPagination = usePagination<
    'createdAt' | 'updatedAt' | 'ledgerDate'
  >({
    id: 'post-unrealised-cash-fx-gains-confirmed',
    order: SortOrder.Desc,
    orderBy: 'ledgerDate',
  });

  const overridesEntry =
    currencyRateOverrides.size > 0
      ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
      : {};

  const queryParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? new CalendarDay(new Date()),
    periodEnd: periodEnd ?? new CalendarDay(new Date()),
    viewDate: periodEnd ?? new CalendarDay(new Date()),
  };

  const { data: period } = api.accountingPeriod.getPeriodById.useQuery({
    vehicleId: selectedVehicleId ?? '',
    id: periodId ?? '',
  });

  const isConfirmed =
    !!period?.checklist?.unrealisedFxGainsAndLosses?.confirmed;

  const { data: payablesData, isFetching: payablesIsFetching } =
    api.unrealisedFxGainOrLoss.list.useQuery({
      ...queryParams,
      page: payablesPagination.page,
      rowsPerPage: payablesPagination.rowsPerPage,
      order: payablesPagination.order,
      orderBy: payablesPagination.orderBy,
      ...overridesEntry,
    });

  const { data: cashData, isFetching: cashIsFetching } =
    api.unrealisedCashFxGainOrLoss.list.useQuery({
      ...queryParams,
      ...overridesEntry,
    });

  const confirmedQueryEnabled =
    isConfirmed && !!periodId && !!selectedVehicleId;

  const { data: confirmedPayablesData, isFetching: confirmedPayablesLoading } =
    api.unrealisedFxGainOrLoss.listPosted.useQuery(
      {
        vehicleId: selectedVehicleId ?? '',
        periodId,
        page: confirmedPayablesPagination.page,
        rowsPerPage: confirmedPayablesPagination.rowsPerPage,
        order: confirmedPayablesPagination.order,
        orderBy: confirmedPayablesPagination.orderBy,
      },
      { enabled: confirmedQueryEnabled },
    );

  const { data: confirmedCashData, isFetching: confirmedCashLoading } =
    api.unrealisedCashFxGainOrLoss.listPosted.useQuery(
      {
        vehicleId: selectedVehicleId ?? '',
        periodId,
        page: confirmedCashPagination.page,
        rowsPerPage: confirmedCashPagination.rowsPerPage,
        order: confirmedCashPagination.order,
        orderBy: confirmedCashPagination.orderBy,
      },
      { enabled: confirmedQueryEnabled },
    );

  return {
    periodId,
    periodEnd,
    isConfirmed,

    payablesData,
    payablesIsFetching,
    payablesPagination,

    cashData,
    cashIsFetching,

    cashCurrencyRates: cashData?.currencyRates ?? [],
    payablesCurrencyRates: payablesData?.viewDateCurrencyRates ?? [],

    confirmedPayablesData,
    confirmedPayablesLoading,
    confirmedPayablesPagination,

    confirmedCashData,
    confirmedCashLoading,
    confirmedCashPagination,
  };
}
